import React from 'react';

import type { ChipProps } from '@mui/material/Chip';
import MuiChip from '@mui/material/Chip';

import { colours } from 'src/constants/colours';
import theme from 'src/utils/theme';
import { styledComponent } from 'src/utils/styled';

export interface TagProps extends Omit<ChipProps, 'color' | 'size'> {
    backgroundColor?: string;
    textColor?: string;
    size?: 'midsmall' | 'small' | 'tiny' | 'medium';
    color?: ChipProps['color'] | 'tertiary' | 'success';
}

const IntermediateChip: React.FC<TagProps> = ({ children, backgroundColor, textColor, size, ...rest }) => (
    <MuiChip {...(rest as ChipProps)}>{children}</MuiChip>
);

const Tag = styledComponent(IntermediateChip)<TagProps>`
    &.MuiChip-root {
        border-radius: 10rem;
        border: none;
        background-color: ${({ backgroundColor }) => backgroundColor};
        svg {
            color: ${({ textColor }) => textColor};
        }
        .MuiChip-label {
            color: ${({ textColor }) => textColor};
            line-height: 1.125rem;
            font-size: 0.875rem;
            padding: 0 0.5rem;
        }
    }
    .MuiChip-icon {
        margin-left: 0.5rem;
        color: currentColor;
    }

    /* default color */
    color: ${theme.palette.primary.main};
    background-color: ${colours.primary.O08};
    border: 0.0625rem solid ${colours.primary.O08};

    /* color="primary" */
    ${(props: TagProps) =>
        props.color === 'primary' &&
        `
        color: ${theme.palette.primary.contrastText};
        background-color: ${theme.palette.primary.main};
    `}

    /* color="secondary" */
    ${(props: TagProps) =>
        props.color === 'secondary' &&
        `
        color: ${theme.palette.secondary.main};
        background-color: ${colours.secondaryOpaque.O12};
    `}

    /* color="tertiary" */
    ${(props: TagProps) =>
        props.color === 'tertiary' &&
        `
        color: ${theme.palette.tertiary.main};
        background-color: ${colours.tertiaryOpaque.O12};

    `}

    /* color="success" */
    ${(props: TagProps) =>
        props.color === 'success' &&
        `
        color: ${colours.greenSuccess};
        background-color: ${colours.successOpaque.O12};
    `}

    /* size="midsmall" */
    ${(props: TagProps) =>
        props.size === 'midsmall' &&
        `
        height: 1.75rem;
        font-size: 0.875rem;
    `}

    /* size="small" */
    ${(props: TagProps) =>
        props.size === 'small' &&
        `
        height: 1.5rem;
        font-size: 0.66rem;
    `}

    /* size="tiny" */
    ${(props: TagProps) =>
        props.size === 'tiny' &&
        `
        height: 1.25rem;
        font-size: 0.875rem;
    `}

`;

export default Tag;
